import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import {
  Plus,
  Edit,
  Trash2,
  BookOpen,
  FileText,
  CheckCircle2,
  Save,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  RefreshCw,
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  X,
} from "lucide-react";
import { toast } from "sonner";

const ManualContentEditor = ({ existingPassage, onSaved, onCancel }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [lastSavedPassageId, setLastSavedPassageId] = useState(null);

  // Passage form state - matches AI Content Studio structure
  const [passageForm, setPassageForm] = useState({
    title: "",
    content: "",
    summary: "",
    category: "general",
    passage_type: "static",
    mode: "school",
    difficulty: 2,
    vocabulary: [],
    status: "draft",
  });

  // Questions array
  const [questions, setQuestions] = useState([]);

  // New vocabulary input
  const [newVocab, setNewVocab] = useState({ word: "", meaning: "" });

  // Initialize from existing passage if editing
  useEffect(() => {
    if (existingPassage) {
      setPassageForm({
        title: existingPassage.title || "",
        content: existingPassage.content || "",
        summary: existingPassage.summary || "",
        category: existingPassage.category || "general",
        passage_type: existingPassage.content_type || "static",
        mode: existingPassage.exam_modes?.[0] || "school",
        difficulty: existingPassage.difficulty || 2,
        vocabulary: existingPassage.vocabulary || [],
        status: existingPassage.status || "draft",
      });
      // Fetch existing questions
      fetchQuestions(existingPassage.passage_id);
    }
  }, [existingPassage]);

  const fetchQuestions = async (passageId) => {
    try {
      const response = await axios.get(`${API}/admin/questions/${passageId}`, {
        headers,
        withCredentials: true,
      });
      setQuestions(response.data.map(q => ({
        ...q,
        isNew: false,
      })));
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const modes = [
    { id: "children", name: "Children (6-10)", desc: "Very simple, story-based" },
    { id: "school", name: "School (11-16)", desc: "Simple to moderate" },
    { id: "ssc", name: "SSC", desc: "Moderate to advanced" },
    { id: "banking", name: "Banking", desc: "Professional, finance-focused" },
    { id: "upsc", name: "UPSC Editorial", desc: "Sophisticated, analytical" },
  ];

  const categories = [
    { id: "general", name: "General" },
    { id: "economy", name: "Economy & Business" },
    { id: "polity", name: "Polity & Governance" },
    { id: "science", name: "Science & Technology" },
    { id: "history", name: "History & Culture" },
    { id: "environment", name: "Environment" },
    { id: "social", name: "Social Issues" },
    { id: "editorial", name: "Editorials" },
    { id: "story", name: "Stories" },
    { id: "biography", name: "Biographies" },
  ];

  const questionTypes = [
    { id: "direct", name: "Direct" },
    { id: "inference", name: "Inference" },
    { id: "vocabulary", name: "Vocabulary" },
    { id: "tone", name: "Tone/Attitude" },
    { id: "argument", name: "Argument" },
    { id: "summary", name: "Summary/Main Idea" },
  ];

  const difficultyLabels = ["Very Easy", "Easy", "Medium", "Hard", "Very Hard"];

  const addNewQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: `new_${Date.now()}`,
        question_text: "",
        options: ["", "", "", ""],
        correct_option: 0,
        question_type: "direct",
        explanation: "",
        why_others_wrong: ["", "", "", ""],
        isNew: true,
      },
    ]);
    setExpandedQuestion(questions.length);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const updateQuestionOption = (qIndex, oIndex, value) => {
    const updated = [...questions];
    const options = [...updated[qIndex].options];
    options[oIndex] = value;
    updated[qIndex] = { ...updated[qIndex], options };
    setQuestions(updated);
  };

  const updateWhyWrong = (qIndex, oIndex, value) => {
    const updated = [...questions];
    const whyWrong = [...(updated[qIndex].why_others_wrong || ["", "", "", ""])];
    whyWrong[oIndex] = value;
    updated[qIndex] = { ...updated[qIndex], why_others_wrong: whyWrong };
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    if (window.confirm("Remove this question?")) {
      setQuestions(questions.filter((_, i) => i !== index));
      if (expandedQuestion === index) setExpandedQuestion(null);
    }
  };

  const addVocabulary = () => {
    if (newVocab.word && newVocab.meaning) {
      setPassageForm({
        ...passageForm,
        vocabulary: [...passageForm.vocabulary, { ...newVocab }],
      });
      setNewVocab({ word: "", meaning: "" });
    }
  };

  const removeVocabulary = (index) => {
    setPassageForm({
      ...passageForm,
      vocabulary: passageForm.vocabulary.filter((_, i) => i !== index),
    });
  };

  const generateAISummary = async () => {
    if (!passageForm.content) {
      toast.error("Add passage content first");
      return;
    }
    setGeneratingAI("summary");
    try {
      const response = await axios.post(
        `${API}/ai/generate`,
        { text: passageForm.content, type: "summary" },
        { headers, withCredentials: true, timeout: 30000 }
      );
      setPassageForm({ ...passageForm, summary: response.data.content });
      toast.success("Summary generated!");
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Failed to generate summary");
    } finally {
      setGeneratingAI(null);
    }
  };

  const generateAIExplanation = async (qIndex) => {
    const q = questions[qIndex];
    if (!q.question_text || !q.options[q.correct_option]) {
      toast.error("Add question and correct answer first");
      return;
    }
    setGeneratingAI(`explanation_${qIndex}`);
    try {
      const text = `Question: ${q.question_text}\nCorrect Answer: ${q.options[q.correct_option]}\nPassage context: ${passageForm.content?.substring(0, 500)}`;
      const response = await axios.post(
        `${API}/ai/generate`,
        { text, type: "explanation" },
        { headers, withCredentials: true, timeout: 30000 }
      );
      updateQuestion(qIndex, "explanation", response.data.content);
      toast.success("Explanation generated!");
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Failed to generate explanation");
    } finally {
      setGeneratingAI(null);
    }
  };

  const handleSave = async (newStatus = passageForm.status) => {
    // Validate
    if (!passageForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!passageForm.content.trim()) {
      toast.error("Passage content is required");
      return;
    }
    if (questions.length === 0) {
      toast.error("Add at least one question");
      return;
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) {
        toast.error(`Question ${i + 1} text is required`);
        return;
      }
      if (q.options.some((o) => !o.trim())) {
        toast.error(`Question ${i + 1} must have all 4 options filled`);
        return;
      }
    }

    setSaving(true);
    try {
      // Determine exam_modes based on mode
      const modeMapping = {
        children: ["school"],
        school: ["school", "college"],
        ssc: ["ssc", "banking"],
        banking: ["banking", "ssc"],
        upsc: ["upsc"],
        general: ["general"],
      };
      const exam_modes = modeMapping[passageForm.mode] || ["general"];

      // Prepare passage data
      const passageData = {
        title: passageForm.title,
        content: passageForm.content,
        category: passageForm.category,
        content_type: passageForm.passage_type,
        difficulty: passageForm.difficulty,
        exam_modes,
        tags: [],
        summary: passageForm.summary || null,
        vocabulary: passageForm.vocabulary,
        status: newStatus,
      };

      let passageId;

      if (existingPassage) {
        // Update existing passage
        await axios.put(
          `${API}/admin/passages/${existingPassage.passage_id}`,
          passageData,
          { headers, withCredentials: true }
        );
        passageId = existingPassage.passage_id;

        // Delete existing questions and recreate them
        for (const q of questions) {
          if (q.question_id && !q.isNew) {
            await axios.delete(`${API}/admin/questions/${q.question_id}`, {
              headers,
              withCredentials: true,
            });
          }
        }
      } else {
        // Create new passage
        const response = await axios.post(`${API}/admin/passages`, passageData, {
          headers,
          withCredentials: true,
        });
        passageId = response.data.passage_id;
      }

      // Create all questions
      for (const q of questions) {
        const questionData = {
          passage_id: passageId,
          question_text: q.question_text,
          options: q.options,
          correct_option: q.correct_option,
          question_type: q.question_type,
          explanation: q.explanation,
          why_others_wrong: q.why_others_wrong,
        };
        await axios.post(`${API}/admin/questions`, questionData, {
          headers,
          withCredentials: true,
        });
      }

      toast.success(
        existingPassage
          ? "Passage updated successfully!"
          : `Passage saved as ${newStatus}!`
      );
      
      setLastSavedPassageId(passageId);
      setShowSaveSuccess(true);
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Handle "View in List" click
  const handleViewInList = () => {
    setShowSaveSuccess(false);
    if (onSaved) onSaved();
  };

  // Handle "Create Another" click
  const handleCreateAnother = () => {
    setShowSaveSuccess(false);
    setLastSavedPassageId(null);
    // Reset form
    setPassageForm({
      title: "",
      content: "",
      summary: "",
      category: "general",
      passage_type: "static",
      mode: "school",
      difficulty: 2,
      vocabulary: [],
      status: "draft",
    });
    setQuestions([]);
    toast.info("Ready to create another passage");
  };

  // Handle Cancel
  const handleCancelClick = () => {
    if (passageForm.title || passageForm.content || questions.length > 0) {
      if (!window.confirm("Discard unsaved changes?")) return;
    }
    if (onCancel) onCancel();
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button 
          onClick={handleCancelClick}
          className="hover:text-slate-900 transition-colors"
          data-testid="breadcrumb-admin"
        >
          Admin
        </button>
        <ChevronRight className="w-4 h-4" />
        <button 
          onClick={handleCancelClick}
          className="hover:text-slate-900 transition-colors"
          data-testid="breadcrumb-passages"
        >
          Passages
        </button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900 font-medium">
          {existingPassage ? "Edit Passage" : "Create Passage"}
        </span>
      </div>

      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCancelClick}
            className="gap-2"
            data-testid="back-to-passages-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Passages
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-slate-700" />
              {existingPassage ? "Edit Passage" : "Create New Passage"}
            </h2>
            <p className="text-slate-600 mt-1">
              {existingPassage ? "Update passage content and questions" : "Manually create RC passage with questions"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            className={
              passageForm.status === "published"
                ? "bg-emerald-100 text-emerald-700"
                : passageForm.status === "review"
                ? "bg-amber-100 text-amber-700"
                : "bg-slate-100 text-slate-700"
            }
          >
            {passageForm.status.toUpperCase()}
          </Badge>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleCancelClick}
            className="gap-2 text-slate-500 hover:text-slate-700"
            data-testid="cancel-btn"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
        </div>
      </div>

      {/* Main Content Card */}
      <Tabs defaultValue="passage" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="passage">Passage</TabsTrigger>
          <TabsTrigger value="questions">Questions ({questions.length})</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Passage Tab */}
        <TabsContent value="passage">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <Label>Title *</Label>
                <Input
                  value={passageForm.title}
                  onChange={(e) =>
                    setPassageForm({ ...passageForm, title: e.target.value })
                  }
                  placeholder="Enter passage title/headline..."
                  className="mt-1"
                  data-testid="manual-title-input"
                />
              </div>

              <div>
                <Label>Passage Content *</Label>
                <Textarea
                  value={passageForm.content}
                  onChange={(e) =>
                    setPassageForm({ ...passageForm, content: e.target.value })
                  }
                  placeholder="Enter the full passage text..."
                  className="mt-1 min-h-[300px] font-serif text-lg leading-relaxed"
                  data-testid="manual-content-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {passageForm.content.length} characters
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Summary (Optional)</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={generateAISummary}
                    disabled={generatingAI === "summary"}
                    data-testid="generate-summary-btn"
                  >
                    {generatingAI === "summary" ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Generate with AI
                  </Button>
                </div>
                <Textarea
                  value={passageForm.summary}
                  onChange={(e) =>
                    setPassageForm({ ...passageForm, summary: e.target.value })
                  }
                  placeholder="Brief 2-3 sentence summary..."
                  className="min-h-[80px]"
                />
              </div>

              {/* Vocabulary */}
              <div>
                <Label>Key Vocabulary (Optional)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Word"
                    value={newVocab.word}
                    onChange={(e) =>
                      setNewVocab({ ...newVocab, word: e.target.value })
                    }
                    className="w-32"
                  />
                  <Input
                    placeholder="Meaning"
                    value={newVocab.meaning}
                    onChange={(e) =>
                      setNewVocab({ ...newVocab, meaning: e.target.value })
                    }
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addVocabulary}
                    disabled={!newVocab.word || !newVocab.meaning}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {passageForm.vocabulary.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {passageForm.vocabulary.map((v, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-sky-50 border border-sky-200 rounded-lg text-sm flex items-center gap-2"
                      >
                        <strong>{v.word}</strong>: {v.meaning}
                        <button
                          onClick={() => removeVocabulary(i)}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Questions</CardTitle>
                <Button onClick={addNewQuestion} data-testid="add-question-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>
              <CardDescription>
                Add 3-8 questions with 4 options each. Select the correct answer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {questions.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No questions yet</p>
                  <Button onClick={addNewQuestion} variant="link" className="mt-2">
                    Add your first question
                  </Button>
                </div>
              ) : (
                questions.map((q, qIndex) => (
                  <div
                    key={q.id || q.question_id}
                    className="border rounded-xl overflow-hidden"
                    data-testid={`question-card-${qIndex}`}
                  >
                    <button
                      onClick={() =>
                        setExpandedQuestion(
                          expandedQuestion === qIndex ? null : qIndex
                        )
                      }
                      className="w-full flex items-center justify-between p-4 bg-slate-50 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold">
                          {qIndex + 1}
                        </span>
                        <div>
                          <p className="font-medium text-slate-900 line-clamp-1">
                            {q.question_text || "New Question"}
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {questionTypes.find((t) => t.id === q.question_type)?.name ||
                                q.question_type}
                            </Badge>
                            {q.options[q.correct_option] && (
                              <Badge className="text-xs bg-emerald-100 text-emerald-700">
                                Answer: {String.fromCharCode(65 + q.correct_option)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeQuestion(qIndex);
                          }}
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedQuestion === qIndex ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {expandedQuestion === qIndex && (
                      <div className="p-4 space-y-4 animate-fade-in border-t">
                        {/* Question Text */}
                        <div>
                          <Label>Question Text *</Label>
                          <Textarea
                            value={q.question_text}
                            onChange={(e) =>
                              updateQuestion(qIndex, "question_text", e.target.value)
                            }
                            placeholder="Enter the question..."
                            className="mt-1"
                            data-testid={`question-text-${qIndex}`}
                          />
                        </div>

                        {/* Question Type */}
                        <div>
                          <Label>Question Type</Label>
                          <Select
                            value={q.question_type}
                            onValueChange={(v) =>
                              updateQuestion(qIndex, "question_type", v)
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {questionTypes.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Options */}
                        <div>
                          <Label>Options * (Select correct answer)</Label>
                          <div className="space-y-2 mt-2">
                            {q.options.map((opt, oIndex) => (
                              <div
                                key={oIndex}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                                  oIndex === q.correct_option
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-slate-200 bg-white"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`correct-${qIndex}`}
                                  checked={oIndex === q.correct_option}
                                  onChange={() =>
                                    updateQuestion(qIndex, "correct_option", oIndex)
                                  }
                                  className="w-4 h-4 accent-emerald-600"
                                />
                                <span className="font-bold text-slate-500 w-6">
                                  {String.fromCharCode(65 + oIndex)}.
                                </span>
                                <Input
                                  value={opt}
                                  onChange={(e) =>
                                    updateQuestionOption(qIndex, oIndex, e.target.value)
                                  }
                                  placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                                  className="flex-1"
                                  data-testid={`option-${qIndex}-${oIndex}`}
                                />
                                {oIndex === q.correct_option && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                )}
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Click the radio button to mark the correct answer
                          </p>
                        </div>

                        {/* Explanation */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Explanation *</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => generateAIExplanation(qIndex)}
                              disabled={generatingAI === `explanation_${qIndex}`}
                              data-testid={`generate-explanation-${qIndex}`}
                            >
                              {generatingAI === `explanation_${qIndex}` ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-1" />
                              )}
                              Generate with AI
                            </Button>
                          </div>
                          <Textarea
                            value={q.explanation}
                            onChange={(e) =>
                              updateQuestion(qIndex, "explanation", e.target.value)
                            }
                            placeholder="Explain why this answer is correct..."
                            className="min-h-[100px]"
                            data-testid={`explanation-${qIndex}`}
                          />
                        </div>

                        {/* Why Others Wrong (Optional) */}
                        <div className="bg-rose-50 p-4 rounded-lg">
                          <Label className="text-rose-700">
                            Why Other Options Are Wrong (Optional)
                          </Label>
                          <div className="space-y-2 mt-2">
                            {q.options.map((_, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <span className="font-bold text-slate-500 w-6">
                                  {String.fromCharCode(65 + oIndex)}:
                                </span>
                                {oIndex === q.correct_option ? (
                                  <span className="text-emerald-600 text-sm">
                                    (Correct answer)
                                  </span>
                                ) : (
                                  <Input
                                    value={q.why_others_wrong?.[oIndex] || ""}
                                    onChange={(e) =>
                                      updateWhyWrong(qIndex, oIndex, e.target.value)
                                    }
                                    placeholder={`Why ${String.fromCharCode(65 + oIndex)} is wrong...`}
                                    className="flex-1 text-sm"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Mode Selection */}
                <div>
                  <Label>Target Mode *</Label>
                  <Select
                    value={passageForm.mode}
                    onValueChange={(v) =>
                      setPassageForm({ ...passageForm, mode: v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {modes.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          <div>
                            <span className="font-medium">{m.name}</span>
                            <span className="text-slate-500 ml-2 text-xs">
                              {m.desc}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={passageForm.category}
                    onValueChange={(v) =>
                      setPassageForm({ ...passageForm, category: v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Difficulty */}
                <div className="md:col-span-2">
                  <Label>
                    Difficulty Level:{" "}
                    <span className="font-bold">
                      {difficultyLabels[passageForm.difficulty]}
                    </span>
                  </Label>
                  <Slider
                    value={[passageForm.difficulty]}
                    onValueChange={([v]) =>
                      setPassageForm({ ...passageForm, difficulty: v })
                    }
                    min={0}
                    max={4}
                    step={1}
                    className="mt-3"
                    data-testid="difficulty-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    {difficultyLabels.map((label, i) => (
                      <span key={i}>{label}</span>
                    ))}
                  </div>
                </div>

                {/* Passage Type */}
                <div>
                  <Label>Content Type</Label>
                  <Select
                    value={passageForm.passage_type}
                    onValueChange={(v) =>
                      setPassageForm({ ...passageForm, passage_type: v })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="static">Static (Evergreen)</SelectItem>
                      <SelectItem value="dynamic">Dynamic (News)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status */}
                <div>
                  <Label>Status</Label>
                  <Select
                    value={passageForm.status}
                    onValueChange={(v) =>
                      setPassageForm({ ...passageForm, status: v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="review">In Review</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Buttons */}
      <div className="flex justify-between items-center p-4 bg-slate-100 rounded-xl">
        <Button 
          variant="outline" 
          onClick={handleCancelClick} 
          className="gap-2"
          data-testid="footer-cancel-btn"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => handleSave("draft")}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save as Draft
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave("review")}
            disabled={saving}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="submit-review-btn"
          >
            Submit for Review
          </Button>
          <Button
            onClick={() => handleSave("published")}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="publish-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            Publish
          </Button>
        </div>
      </div>

      {/* Workflow Info */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-500">Workflow:</span>
            <Badge
              className={
                passageForm.status === "draft"
                  ? "bg-slate-200 text-slate-700"
                  : "bg-slate-100 text-slate-500"
              }
            >
              DRAFT
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge
              className={
                passageForm.status === "review"
                  ? "bg-amber-200 text-amber-700"
                  : "bg-slate-100 text-slate-500"
              }
            >
              In Review
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge
              className={
                passageForm.status === "published"
                  ? "bg-emerald-200 text-emerald-700"
                  : "bg-slate-100 text-slate-500"
              }
            >
              Published
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Save Success Modal */}
      {showSaveSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {existingPassage ? "Passage Updated!" : "Passage Saved!"}
              </h3>
              <p className="text-slate-600 mb-6">
                Your passage &ldquo;{passageForm.title}&rdquo; has been saved as <strong>{passageForm.status}</strong>.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={handleViewInList}
                  className="gap-2"
                  data-testid="view-in-list-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  View in List
                </Button>
                {!existingPassage && (
                  <Button 
                    onClick={handleCreateAnother}
                    className="gap-2"
                    data-testid="create-another-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create Another
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ManualContentEditor;
